import './taken-user-info-button.scss';

import { Alert, Spin, notification } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import {
  Controller,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form';

import { Button, Input, Modal, Select } from '@/components/Shared/ui';
import type { ISelectOption } from '@/components/Shared/ui/select/select';
import { DictTypes } from '@/constants';
import { userApi } from '@/integrations/adminApi/usersApi';
import { autoCreditApi } from '@/integrations/app.api/auto-credit.api';
import { handleError } from '@/services/handleError';
import { isDictHasAll } from '@/services/helpers/dict';
import { getUserFullName } from '@/services/helpers/strings';
import { dispatch, useAppSelector } from '@/store';
import { getRequestById } from '@/store/newOrder.slice';
import type { TUserResponse } from '@/types/user.type';

type TForm = {
  email: string;
  lastName: string;
  userId: string;
};

const TakenUserInfoButton: React.FC = () => {
  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { userInfo } = useAppSelector((state) => state.userInfo);
  const {
    userAccesses: {
      autoCredit: { canReassignOnlineRequest }
    }
  } = useAppSelector((state) => state.userInfo);

  const { onlineClientRequest } = orderApp || {};
  const { takenByUser } = onlineClientRequest || {};

  const isTakenByCurrentUser =
    !!takenByUser && takenByUser.uuid === userInfo?.uuid;

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingUserOptions, setIsLoadingUserOptions] = useState(false);
  const [users, setUsers] = useState<
    Pick<
      TUserResponse,
      'uuid' | 'email' | 'first_name' | 'last_name' | 'login' | 'middle_name'
    >[]
  >([]);

  const { control, handleSubmit, reset } = useForm<TForm>({
    delayError: 200,
    mode: 'all'
  });

  const formValues = useWatch({ control });

  const dealer = useMemo(() => {
    if (
      !orderApp ||
      !orderApp.productInfo.product.dealer ||
      !isDictHasAll(dicts, [DictTypes.Dealers])
    )
      return null;
    return (
      dicts[DictTypes.Dealers].getBy.code[
        orderApp.productInfo.product.dealer
      ] ?? null
    );
  }, [dicts, orderApp]);

  const filteredUserOptions: ISelectOption[] = useMemo(
    () =>
      users
        .filter(
          (user) =>
            (!formValues.email ||
              (user.email ?? '')
                .toLowerCase()
                .includes(formValues.email.toLowerCase())) &&
            (!formValues.lastName ||
              (user.last_name ?? '')
                .toLowerCase()
                .includes(formValues.lastName.toLowerCase()))
        )
        .map((user) => ({
          label: `${getUserFullName(user)} (${user.email})`,
          value: user.uuid
        })),
    [formValues.email, formValues.lastName, users]
  );

  const onOk: SubmitHandler<TForm> = async (data) => {
    const requestId = orderApp?.onlineClientRequest?.uuid;

    if (!requestId || !data.userId) return;

    try {
      setIsLoading(true);
      await autoCreditApi.reassignOnlineRequest({
        requestId,
        newUserId: data.userId
      });
      await dispatch(getRequestById({ reqId: orderApp.uuid }));
      notification.success({ title: 'Сотрудник успешно назначен' });
      reset({});
      setUsers([]);
      setIsModalOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen && dealer) {
      (async () => {
        setIsLoadingUserOptions(true);
        try {
          const users = await userApi.getUsersByDealer(dealer.uuid);
          setUsers(
            users.sort((userA, userB) =>
              getUserFullName(userA).localeCompare(getUserFullName(userB))
            )
          );
        } catch (error) {
          handleError(error);
        } finally {
          setIsLoadingUserOptions(false);
        }
      })();
    } else {
      reset();
      setUsers([]);
    }
  }, [isModalOpen, dealer]);

  return (
    <>
      {isTakenByCurrentUser && canReassignOnlineRequest ? (
        <Button
          variant="filled-blue"
          className="taken-user-info-button__alert-button"
          size="small"
          onClick={() => setIsModalOpen(true)}
        >
          Переназначить
        </Button>
      ) : (
        <Alert
          className="taken-user-info-button__alert"
          description={
            <span>
              Заявка взята в работу сотрудником{' '}
              <b>
                {getUserFullName(
                  orderApp?.onlineClientRequest?.takenByUser || {}
                )}
              </b>
            </span>
          }
          action={
            canReassignOnlineRequest && (
              <Button
                variant="filled-blue"
                className="taken-user-info-button__alert-button"
                size="small"
                onClick={() => setIsModalOpen(true)}
              >
                Переназначить
              </Button>
            )
          }
        />
      )}
      {canReassignOnlineRequest && (
        <Modal
          title="Переназначить заявку"
          open={isModalOpen}
          closable={!isLoading}
          onClose={() => setIsModalOpen(false)}
        >
          {isLoadingUserOptions ? (
            <div className="taken-user-info-button__loader-container">
              <Spin />
            </div>
          ) : (
            <>
              {!!dealer && (
                <p>
                  Сотрудники дилерского центра <b>{dealer.name}</b>:
                </p>
              )}
              <form
                className="taken-user-info-button__form"
                onSubmit={(e) => {
                  e.stopPropagation();
                  handleSubmit(onOk)(e);
                }}
              >
                <span className="taken-user-info-button__divider">
                  Фильтрация сотрудников
                </span>
                <Controller
                  control={control}
                  name="lastName"
                  render={({ field }) => <Input {...field} label="Фамилия" />}
                />
                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => <Input {...field} label="Email" />}
                />

                <span className="taken-user-info-button__divider">
                  Назначить на
                </span>
                <Controller
                  control={control}
                  name="userId"
                  rules={{ required: 'Пожалуйста, выберите сотрудника' }}
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      isRequired
                      label={`Сотрудник (Всего: ${filteredUserOptions.length})`}
                      selectedValue={filteredUserOptions.find(
                        (item) => item.value === field.value
                      )}
                      onChange={(status) => {
                        field.onChange(status.value);
                      }}
                      options={filteredUserOptions}
                      isFrozen={isLoading}
                      error={error?.message}
                    />
                  )}
                />
                <div className="taken-user-info-button__actions">
                  <Button
                    onClick={() => setIsModalOpen(false)}
                    type="button"
                    disabled={isLoading}
                  >
                    Отменить
                  </Button>
                  <Button
                    variant="filled-red"
                    type="submit"
                    isLoading={isLoading}
                    disabled={!formValues.userId}
                  >
                    Переназначить
                  </Button>
                </div>
              </form>
            </>
          )}
        </Modal>
      )}
    </>
  );
};

export default TakenUserInfoButton;
